import {
  Circle,
  Document,
  Ellipse,
  G,
  Line,
  Page,
  Path,
  Polygon,
  Polyline,
  Rect,
  Svg,
  Text,
  View,
} from '@react-pdf/renderer';
import type { SVGPresentationAttributes } from '@react-pdf/types';

import { type PdfFontSourceResolver, registerPdfFonts } from './fonts.ts';
import type { PdfInvoiceItem, PdfInvoiceModel, PdfParty } from './model.ts';
import type { PdfSignature, PdfSignatureNode, SvgAttrs } from './signature.ts';
import { PDF_SIGNATURE_LAYOUT, pdfStyles } from './styles.ts';
import { formatCurrencyCents, parseCanonicalDecimal } from '../../shared/format.ts';
import type { Currency } from '../../shared/invoice.ts';

export interface InvoiceDocumentProps {
  invoice: PdfInvoiceModel;
  signature?: PdfSignature | null;
  fontSourceResolver?: PdfFontSourceResolver;
}

type SignaturePresentationAttributes = SVGPresentationAttributes & {
  strokeMiterlimit?: string | number;
};

const DATE_MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
] as const;

export function InvoiceDocument({
  invoice,
  signature = null,
  fontSourceResolver,
}: InvoiceDocumentProps) {
  registerPdfFonts(fontSourceResolver ? { sourceResolver: fontSourceResolver } : undefined);

  return (
    <Document title={`Invoice ${invoice.number}`} creator="invoice-generator">
      <Page size="A4" style={pdfStyles.page}>
        <View style={pdfStyles.header}>
          <View style={pdfStyles.senderColumn}>
            <Text style={pdfStyles.eyebrow}>From</Text>
            <Text style={pdfStyles.senderName}>{invoice.sender.name}</Text>
            {signature ? <SignatureGraphic signature={signature} /> : null}
            {invoice.sender.bankRequisites.trim() ? (
              <BankDetails requisites={invoice.sender.bankRequisites} />
            ) : null}
          </View>

          <View style={pdfStyles.titleColumn}>
            <Text style={pdfStyles.eyebrow}>Invoice</Text>
            <Text style={pdfStyles.title}>#{invoice.number}</Text>
            <View style={pdfStyles.titleRule} />
            <View style={pdfStyles.metadata}>
              <MetadataRow label="Date" value={formatPdfDate(invoice.date)} />
              {invoice.dueDate ? (
                <MetadataRow label="Due Date" value={formatPdfDate(invoice.dueDate)} />
              ) : null}
            </View>
          </View>
        </View>

        <PartySection billTo={invoice.billTo} shipTo={invoice.shipTo} />
        <ItemsTable items={invoice.items} currency={invoice.currency} />
        <Totals invoice={invoice} />
        {invoice.notes.trim() ? <Notes notes={invoice.notes} /> : null}
      </Page>
    </Document>
  );
}

function MetadataRow({ label, value }: { label: string; value: string }) {
  return (
    <View style={pdfStyles.metadataRow}>
      <Text style={pdfStyles.metadataLabel}>{label}</Text>
      <Text style={pdfStyles.metadataValue}>{value}</Text>
    </View>
  );
}

function PartySection({ billTo, shipTo }: { billTo: PdfParty | null; shipTo: PdfParty | null }) {
  if (!billTo && !shipTo) return null;

  return (
    <View style={pdfStyles.partySection}>
      {billTo ? <PartyBlock title="Bill To" party={billTo} addressKind="billTo" /> : null}
      {shipTo ? (
        <PartyBlock title="Ship To" party={shipTo} addressKind="shipTo" isLast={Boolean(billTo)} />
      ) : null}
    </View>
  );
}

function PartyBlock({
  title,
  party,
  addressKind,
  isLast = false,
}: {
  title: string;
  party: PdfParty;
  addressKind: 'billTo' | 'shipTo';
  isLast?: boolean;
}) {
  const addressLines = splitOptionalLines(party[addressKind]);

  return (
    <View style={isLast ? [pdfStyles.partyBlock, pdfStyles.partyBlockLast] : pdfStyles.partyBlock}>
      <Text style={pdfStyles.eyebrow}>{title}</Text>
      <Text style={pdfStyles.partyName}>{party.name}</Text>
      {party.email ? <Text style={pdfStyles.partyLine}>{party.email}</Text> : null}
      {addressLines.map((line, index) => (
        <Text key={`${line}-${index}`} style={pdfStyles.partyLine}>
          {line}
        </Text>
      ))}
    </View>
  );
}

function ItemsTable({ items, currency }: { items: PdfInvoiceItem[]; currency: Currency }) {
  return (
    <View style={pdfStyles.table}>
      <View style={pdfStyles.tableHeader}>
        <Text style={[pdfStyles.tableHeaderText, pdfStyles.descriptionColumn]}>Description</Text>
        <Text style={[pdfStyles.tableHeaderText, pdfStyles.quantityColumn]}>Quantity</Text>
        <Text style={[pdfStyles.tableHeaderText, pdfStyles.rateColumn]}>Rate</Text>
        <Text style={[pdfStyles.tableHeaderText, pdfStyles.amountColumn]}>Amount</Text>
      </View>
      {items.map((item, index) => (
        <View key={`${item.description}-${index}`} style={pdfStyles.tableRow}>
          <Text style={pdfStyles.descriptionColumn}>{item.description}</Text>
          <Text style={pdfStyles.quantityColumn}>{item.quantity}</Text>
          <Text style={pdfStyles.rateColumn}>{formatRate(item.rate, currency)}</Text>
          <Text style={pdfStyles.amountColumn}>
            {formatCurrencyCents(item.amountCents, currency)}
          </Text>
        </View>
      ))}
    </View>
  );
}

function Totals({ invoice }: { invoice: PdfInvoiceModel }) {
  const taxLabel = formatTaxLabel(invoice.taxRate, invoice.taxLabel);
  return (
    <View style={pdfStyles.totals}>
      <TotalRow
        label="Subtotal"
        value={formatCurrencyCents(invoice.subtotalCents, invoice.currency)}
      />
      <TotalRow label={taxLabel} value={formatCurrencyCents(invoice.taxCents, invoice.currency)} />
      <TotalRow
        label="Total"
        value={formatCurrencyCents(invoice.totalCents, invoice.currency)}
        emphasis
      />
    </View>
  );
}

function formatTaxLabel(taxRate: string, taxLabel: string | null): string {
  const label = taxLabel && taxLabel.trim() !== '' ? taxLabel.trim() : 'Tax';
  return `${label} (${taxRate}%)`;
}

function TotalRow({
  label,
  value,
  emphasis = false,
}: {
  label: string;
  value: string;
  emphasis?: boolean;
}) {
  const rowStyle = emphasis ? [pdfStyles.totalRow, pdfStyles.grandTotalRow] : pdfStyles.totalRow;
  const labelStyle = emphasis
    ? [pdfStyles.totalLabel, pdfStyles.grandTotalLabel]
    : pdfStyles.totalLabel;
  const valueStyle = emphasis
    ? [pdfStyles.totalValue, pdfStyles.grandTotalValue]
    : pdfStyles.totalValue;

  return (
    <View style={rowStyle}>
      <Text style={labelStyle}>{label}</Text>
      <Text style={valueStyle}>{value}</Text>
    </View>
  );
}

function Notes({ notes }: { notes: string }) {
  return (
    <View style={pdfStyles.notesSection}>
      <Text style={pdfStyles.eyebrow}>Notes</Text>
      <Text style={pdfStyles.notesText}>{notes}</Text>
    </View>
  );
}

function BankDetails({ requisites }: { requisites: string }) {
  return (
    <View style={pdfStyles.notesSection}>
      <Text style={pdfStyles.eyebrow}>Bank details</Text>
      <Text style={pdfStyles.notesText}>{requisites.trim()}</Text>
    </View>
  );
}

function SignatureGraphic({ signature }: { signature: PdfSignature }) {
  const height = Math.min(
    PDF_SIGNATURE_LAYOUT.maxHeight,
    (signature.height / signature.width) * PDF_SIGNATURE_LAYOUT.width,
  );

  return (
    <View style={pdfStyles.signatureWrap}>
      <Svg
        width={PDF_SIGNATURE_LAYOUT.width}
        height={height}
        viewBox={signature.viewBox}
        preserveAspectRatio="xMinYMid meet"
        style={pdfStyles.signatureSvg}
      >
        {signature.children.map((node, index) => renderSignatureNode(node, String(index)))}
      </Svg>
    </View>
  );
}

function renderSignatureNode(node: PdfSignatureNode, key: string) {
  const attrs = toPresentationAttrs(node.attrs);

  switch (node.type) {
    case 'g':
      return (
        <G key={key} {...attrs}>
          {node.children.map((child, index) => renderSignatureNode(child, `${key}-${index}`))}
        </G>
      );
    case 'path':
      return <Path key={key} {...attrs} d={node.attrs.d} />;
    case 'rect':
      return (
        <Rect
          key={key}
          {...attrs}
          height={node.attrs.height ?? '0'}
          rx={node.attrs.rx}
          ry={node.attrs.ry}
          width={node.attrs.width ?? '0'}
          x={node.attrs.x}
          y={node.attrs.y}
        />
      );
    case 'circle':
      return (
        <Circle
          key={key}
          {...attrs}
          cx={node.attrs.cx}
          cy={node.attrs.cy}
          r={node.attrs.r ?? '0'}
        />
      );
    case 'ellipse':
      return (
        <Ellipse
          key={key}
          {...attrs}
          cx={node.attrs.cx}
          cy={node.attrs.cy}
          rx={node.attrs.rx ?? '0'}
          ry={node.attrs.ry ?? '0'}
        />
      );
    case 'line':
      return (
        <Line
          key={key}
          {...attrs}
          x1={node.attrs.x1 ?? '0'}
          x2={node.attrs.x2 ?? '0'}
          y1={node.attrs.y1 ?? '0'}
          y2={node.attrs.y2 ?? '0'}
        />
      );
    case 'polyline':
      return <Polyline key={key} {...attrs} points={node.attrs.points ?? ''} />;
    case 'polygon':
      return <Polygon key={key} {...attrs} points={node.attrs.points ?? ''} />;
  }
}

function toPresentationAttrs(attrs: SvgAttrs): SignaturePresentationAttributes {
  const presentation: SignaturePresentationAttributes = {};

  if (attrs.fill) presentation.fill = attrs.fill;
  if (attrs.fillOpacity) presentation.fillOpacity = attrs.fillOpacity;
  if (attrs.fillRule === 'nonzero' || attrs.fillRule === 'evenodd') {
    presentation.fillRule = attrs.fillRule;
  }
  if (attrs.opacity) presentation.opacity = attrs.opacity;
  if (attrs.stroke) presentation.stroke = attrs.stroke;
  if (attrs.strokeDasharray) presentation.strokeDasharray = attrs.strokeDasharray;
  if (
    attrs.strokeLinecap === 'butt' ||
    attrs.strokeLinecap === 'round' ||
    attrs.strokeLinecap === 'square'
  ) {
    presentation.strokeLinecap = attrs.strokeLinecap;
  }
  if (
    attrs.strokeLinejoin === 'bevel' ||
    attrs.strokeLinejoin === 'miter' ||
    attrs.strokeLinejoin === 'round'
  ) {
    presentation.strokeLinejoin = attrs.strokeLinejoin;
  }
  if (attrs.strokeMiterlimit) presentation.strokeMiterlimit = attrs.strokeMiterlimit;
  if (attrs.strokeOpacity) presentation.strokeOpacity = attrs.strokeOpacity;
  if (attrs.strokeWidth) presentation.strokeWidth = attrs.strokeWidth;
  if (attrs.transform) presentation.transform = attrs.transform;

  return presentation;
}

function formatRate(rate: string, currency: Currency): string {
  const parsed = parseCanonicalDecimal(rate, { maxScale: 2, positive: true });
  if (parsed.units > BigInt(Number.MAX_SAFE_INTEGER)) {
    throw new Error('Rate exceeds safe integer range.');
  }
  return formatCurrencyCents(Number(parsed.units), currency);
}

function splitOptionalLines(value: string | null): string[] {
  if (value == null || value.trim() === '') return [];
  return value
    .trim()
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);
}

function formatPdfDate(value: string): string {
  const [year, month, day] = value.split('-').map(Number);
  if (!year || !month || !day) return value;
  const monthName = DATE_MONTHS[month - 1];
  if (!monthName) return value;
  return `${monthName} ${day}, ${year}`;
}
